import { useEffect } from "react";
import { XIcon } from "@phosphor-icons/react";

export const INPUT_CLS =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-colors disabled:bg-slate-50 disabled:text-slate-500";

export function Field({ label, htmlFor, required = false, hint, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-[11px] uppercase tracking-wider text-slate-600 font-semibold mb-1.5"
      >
        {label}
        {required && <span className="text-amber-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && (
        <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
      )}
    </div>
  );
}

export function TextInput({ value, onChange, ...rest }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT_CLS}
      {...rest}
    />
  );
}

export function NumberInput({ value, onChange, ...rest }) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : Number(e.target.value))
      }
      className={INPUT_CLS}
      {...rest}
    />
  );
}

export function DateInput({ value, onChange, ...rest }) {
  return (
    <input
      type="date"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className={INPUT_CLS}
      {...rest}
    />
  );
}

export function TextArea({ value, onChange, rows = 3, ...rest }) {
  return (
    <textarea
      rows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT_CLS}
      {...rest}
    />
  );
}

export function Select({ value, onChange, options, placeholder = "Select…", ...rest }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className={INPUT_CLS}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function PrimaryButton({ children, className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${className}`}
    >
      {children}
    </button>
  );
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  "data-testid": testId,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid={testId}
        className="w-full max-w-lg bg-white border border-slate-200 rounded-lg p-6 animate-fade-in shadow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            {subtitle && (
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                {subtitle}
              </div>
            )}
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close"
            aria-label="Close"
            className="text-slate-500 hover:text-slate-900 rounded-md p-1 hover:bg-slate-100 transition-colors"
          >
            <XIcon size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      data-testid="error-banner"
      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
    >
      {message}
    </div>
  );
}

export function ConfirmDelete({ open, onClose, onConfirm, label, busy }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Delete ${label}?`}
      subtitle="soft delete"
      data-testid="confirm-delete-modal"
    >
      <p className="text-sm text-slate-700">
        This will mark the {label.toLowerCase()} as deleted. It will be hidden
        from lists and detail pages but retained for audit. This action cannot
        be undone in this phase.
      </p>
      <div className="flex justify-end gap-2 pt-5">
        <SecondaryButton type="button" onClick={onClose} data-testid="confirm-delete-cancel">
          Cancel
        </SecondaryButton>
        <DangerButton
          type="button"
          onClick={onConfirm}
          disabled={busy}
          data-testid="confirm-delete-confirm"
        >
          {busy ? "Deleting…" : `Delete ${label}`}
        </DangerButton>
      </div>
    </Modal>
  );
}
